import { dollars } from './buildings';
import {
  isRoadTunnel,
  isTaxiwayRoadTunnel,
  NETWORK_KIND_MASK,
  NetworkKind,
  roadTunnelAxes,
  roadTunnelLink,
} from './ground';

/*
 * Road junctions: where roads (and the motorway) cross, the box of tiles cars share. How a
 * junction is run decides how many cars an hour get through it: by priority (who comes first
 * goes first; turning across the motorway waits for a gap), by traffic lights, by lights that
 * time themselves to the queues, or not at all, on an interchange that lifts the turns over the
 * motorway. Passengers reach the terminal by road, so the narrowest junction on the way in caps
 * how many can arrive (operations.ts, roadArrivals).
 *
 * A junction's kind lives in two spare bits of its road tiles (every tile of the box carries it),
 * so it is saved and hashed with the network and cleared with the road.
 */

/** Kind 3 keeps the name Interchange in code: it is the slip road (see JUNCTION_COST). */
export const JunctionKind = { Priority: 0, Signals: 1, SmartSignals: 2, Interchange: 3 } as const;
export type JunctionKindId = (typeof JunctionKind)[keyof typeof JunctionKind];

export const NETWORK_JUNCTION_SHIFT = 5;
export const NETWORK_JUNCTION_MASK = 0x60;

/** Cars a minute through a junction of each kind (game minutes). */
export const JUNCTION_CARS_PER_MINUTE: readonly number[] = [10, 18, 26, 60];
/**
 * What it costs to set a junction up as each kind (going back to a simpler one is free). The
 * fourth kind was an interchange bridge; it is a slip road now (a lane of its own off the
 * motorway into the airport, no bridge), cheaper and a tower level earlier, as fast.
 */
export const JUNCTION_COST: readonly number[] = [
  0,
  dollars(25_000),
  dollars(70_000),
  dollars(220_000),
];
/** Control tower level each kind needs. */
export const JUNCTION_TOWER: readonly number[] = [0, 1, 2, 3];

/**
 * Cars a minute passing on the motorway, both ways, hour by hour (0–23): quiet at night, rush
 * hours in the morning and the evening. Its traffic takes its share of the junctions on it.
 */
export const MOTORWAY_CARS_BY_HOUR: readonly number[] = [
  8, 6, 5, 5, 6, 12, 24, 44, 48, 36, 26, 24, 26, 26, 28, 34, 44, 52, 48, 34, 26, 20, 14, 10,
];

export function motorwayCars(minuteOfDay: number): number {
  return MOTORWAY_CARS_BY_HOUR[Math.floor(minuteOfDay / 60) % 24] ?? 0;
}

/**
 * Percent of a motorway junction's flow left for the airport's cars while `through` cars a
 * minute pass on the motorway: at plain priority turning traffic needs gaps (fewer as it gets
 * busy); lights split the time between the ways (a little lost at a quiet hour too, on red);
 * self-timing lights waste less of it; a slip road leaves the motorway on a lane of its own.
 */
export function airportSharePercent(kind: number, through: number): number {
  if (kind === JunctionKind.Interchange) return 100;
  if (kind === JunctionKind.SmartSignals) return Math.max(70, 100 - Math.floor((through * 2) / 5));
  if (kind === JunctionKind.Signals) return Math.max(60, 90 - Math.floor(through / 2));
  return Math.max(40, 100 - through);
}

/** Roads are this wide; a tile is in a junction when the road runs wider both ways. */
const ROAD_WIDTH = 4;

/** Tile → junction box id, or -1. Boxes are the connected areas of crossing tiles. */
export function findJunctionBoxes(network: Uint8Array, width: number, height: number): Int32Array {
  const drivable = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    const v = network[y * width + x] as number;
    const k = v & NETWORK_KIND_MASK;
    return (
      k === NetworkKind.Road ||
      k === NetworkKind.Highway ||
      k === NetworkKind.Service ||
      isTaxiwayRoadTunnel(v)
    );
  };
  // A road with a tunnel under it and the tunnel's ramp beside it do not meet (the ramp goes
  // down under the road): no junction there.
  const ramp = (x: number, y: number) =>
    ((network[y * width + x] as number) & NETWORK_KIND_MASK) === NetworkKind.Service;
  // A road's tunnel under a taxiway joins the road only at its ends (see roadTunnelLink).
  const axes = roadTunnelAxes(network, width, height);
  const apart = (ax: number, ay: number, bx: number, by: number) =>
    (isRoadTunnel(network[ay * width + ax] as number) && ramp(bx, by)) ||
    (isRoadTunnel(network[by * width + bx] as number) && ramp(ax, ay)) ||
    !roadTunnelLink(axes, ay * width + ax, by * width + bx, ax !== bx ? 0 : 1);
  // How far the road runs through a tile along one axis (capped: only "wider than a road" matters).
  const run = (x: number, y: number, dx: number, dy: number) => {
    let n = 1;
    for (const s of [1, -1]) {
      for (let i = 1; i <= ROAD_WIDTH; i++) {
        const ax = x + s * dx * (i - 1);
        const ay = y + s * dy * (i - 1);
        const bx = x + s * dx * i;
        const by = y + s * dy * i;
        if (!drivable(bx, by) || apart(ax, ay, bx, by)) break;
        n++;
      }
    }
    return n;
  };
  const crossing = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Never in a tunnel: cars pass straight through below the taxiway.
      if (!drivable(x, y) || isTaxiwayRoadTunnel(network[y * width + x] as number)) continue;
      if (run(x, y, 1, 0) > ROAD_WIDTH && run(x, y, 0, 1) > ROAD_WIDTH) crossing[y * width + x] = 1;
    }
  }
  const boxes = new Int32Array(width * height).fill(-1);
  let next = 0;
  const stack: number[] = [];
  for (let t = 0; t < crossing.length; t++) {
    if (!crossing[t] || boxes[t] !== -1) continue;
    boxes[t] = next;
    stack.push(t);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      const x = c % width;
      const y = (c - x) / width;
      const near = [
        [x + 1, y],
        [x - 1, y],
        [x, y + 1],
        [x, y - 1],
      ] as const;
      for (const [nx, ny] of near) {
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        const n = ny * width + nx;
        if (crossing[n] && boxes[n] === -1) {
          boxes[n] = next;
          stack.push(n);
        }
      }
    }
    next++;
  }
  return boxes;
}

/** The kind a junction box is run as: the highest kind on any of its tiles. */
export function junctionKindOf(
  network: Uint8Array,
  boxes: Int32Array,
  box: number,
): JunctionKindId {
  let kind = 0;
  for (let t = 0; t < boxes.length; t++) {
    if (boxes[t] !== box) continue;
    const k = ((network[t] as number) & NETWORK_JUNCTION_MASK) >> NETWORK_JUNCTION_SHIFT;
    if (k > kind) kind = k;
  }
  return kind as JunctionKindId;
}

/** A tile's own junction bits (0 when not set). */
export function junctionBits(value: number): JunctionKindId {
  return ((value & NETWORK_JUNCTION_MASK) >> NETWORK_JUNCTION_SHIFT) as JunctionKindId;
}
